// Rahmen der App in einfachem JavaScript: Start- und Anmeldeseite, Kopfzeile mit Marke, Offline-Anzeige und Reitern, Hinweise und der Platz für den Inhalt des Reiters.
// Reine Ansichten (`Zustand -> Element`, Werkzeug `plain/kit.js`); Zustand und Aktionen stehen in `app.ts`. Ereignisse laufen über `data-click` (Namen mit Vorsatz `app-`).
import { h, svg } from './plain/kit.js';
import { APP_SHORT_TITLE, APP_TITLE, appIconDataUri, isPopupBlocked } from './brand';
import { LEVEL_LABEL, type CheckLevel, type OfflineReadiness, type ReadinessLevel } from './offline';
import type { HostMode } from './types';

export type TabId = 'ablesung' | 'verwaltung' | 'auswertung' | 'einstellungen' | 'info';
export type VersionCheck = 'checking' | 'updating' | 'done';

export interface ShellState {
  phase: 'starting' | 'signin' | 'app';
  mode: HostMode;
  embedded: boolean;
  pwaOffline: boolean;
  loaded: boolean;
  busy: boolean;
  online: boolean;
  pending: number;
  message: string;
  authError: string;
  /** „Anmelden“ wurde gedrückt, die Weiterleitung zu Microsoft läuft (der Knopf ist dann gesperrt). */
  signingIn: boolean;
  /** Anzeigename des Kontos der letzten Anmeldung auf diesem Gerät („Anmelden als …“). */
  rememberedAccount?: string;
  versionCheck: VersionCheck;
  updateReady: boolean;
  hasSelection: boolean;
  installHint: boolean;
  tab: TabId;
  tabs: Array<{ id: TabId; label: string }>;
  readiness?: OfflineReadiness;
  flyoutOpen: boolean;
  pwaUrl: string;
}

/** Zustand der Offline-Bereitschaft als Live-Anzeige der Baseline (.wb-live): bereit = grün, eingeschränkt = Warnung, nicht möglich = Fehler, nur online = grau */
const LIVE_CLASS: Record<ReadinessLevel, string> = { ready: '', limited: ' warning', unavailable: ' error', 'online-only': ' off' };
const MARKS: Record<CheckLevel, string> = { ok: '✓', warn: '!', fail: '✗', info: 'i' };
const LEVEL_TEXT: Record<CheckLevel, string> = { ok: 'Erfüllt', warn: 'Achtung', fail: 'Nicht erfüllt', info: 'Hinweis' };

function installHint(): HTMLElement {
  return h('div', { class: 'wb-notice info in-page install-hint', role: 'status' },
    h('p', {}, 'Für die Erfassung ohne Netz diese Seite als App installieren: In der Safari-Leiste auf „Teilen“ ',
      svg('svg', { class: 'card-icon', viewBox: '0 0 24 24', 'aria-hidden': 'true' },
        ['M12 3v12', 'M8 7l4-4 4 4', 'M7 11H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1'].map(d => svg('path', { d }))),
      ' tippen und „Zum Home-Bildschirm“ wählen. Danach die App über das Symbol auf dem Home-Bildschirm öffnen und dort anmelden.'),
    h('button', { type: 'button', class: 'wb-secondary small', 'data-click': 'app-hide-hint' }, 'Hinweis ausblenden'));
}

function offlineStatus(s: ShellState): Array<HTMLElement | null> {
  if (!s.loaded) {
    return [h('span', { class: 'wb-live off', role: 'status' },
      h('span', { class: 'ready-label ready-full' }, 'Offline: wird geprüft …'),
      h('span', { class: 'ready-label ready-short', 'aria-hidden': 'true' }, s.online ? 'Online' : 'Offline'))];
  }
  const readiness = s.readiness;
  if (!readiness) return [null];
  const checkedAt = new Date(readiness.checkedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const chip = h('button', {
    type: 'button', class: `wb-live${LIVE_CLASS[readiness.level]}`, 'aria-expanded': String(s.flyoutOpen), 'aria-controls': 'offline-panel',
    title: `${readiness.headline}, ${s.online ? 'online' : 'offline'}`, 'data-click': 'app-flyout'
  }, h('span', { class: 'ready-label ready-full' }, LEVEL_LABEL[readiness.level]), h('span', { class: 'ready-label ready-short', 'aria-hidden': 'true' }, s.online ? 'Online' : 'Offline'));
  if (!s.flyoutOpen) return [chip];
  const panel = h('section', { id: 'offline-panel', class: 'wb-flyout', 'aria-label': 'Offline-Bereitschaft' },
    h('div', { class: 'ready-head' }, h('strong', {}, readiness.headline)),
    h('ul', { class: 'wb-checklist' }, readiness.checks.map(check => h('li', { class: check.level },
      h('span', { class: 'mark', 'aria-hidden': 'true' }, MARKS[check.level]),
      h('div', {}, h('strong', {}, h('span', { class: 'sr-only' }, `${LEVEL_TEXT[check.level]}: `), check.title), h('span', { class: 'ready-detail' }, check.detail))))),
    h('div', { class: 'ready-pwa' },
      s.pwaUrl && h('small', { class: 'ready-more' }, `Adresse der installierbaren App (PWA): ${s.pwaUrl}`),
      h('small', { class: 'ready-more' }, 'Die schon installierte App startest du über das Symbol auf dem Home-Bildschirm. Der Link öffnet je nach Gerät nur eine Browserseite, nicht die installierte App.'),
      h('div', { class: 'wb-actions' }, h('button', { type: 'button', class: 'wb-secondary', 'data-click': 'app-copy-link' }, 'Link kopieren'))),
    readiness.level !== 'online-only' && h('small', { class: 'ready-foot' }, `Geprüft um ${checkedAt} Uhr.`,
      h('span', { class: 'ready-more' }, ' Die Anzeige prüft die Voraussetzungen auf diesem Gerät. Den sicheren Nachweis liefert ein Test im Flugmodus.')));
  return [chip, panel];
}

function brand(s: ShellState): HTMLElement | null {
  if (s.embedded) return null;
  return h('div', { class: 'brand' },
    h('img', { class: 'app-icon', src: appIconDataUri, alt: '' }),
    h('h1', {}, APP_SHORT_TITLE),
    s.pwaOffline && offlineStatus(s),
    s.pwaOffline && h('button', { type: 'button', class: 'wb-secondary wb-icon-button app-refresh', title: 'App aktualisieren (neu laden)', 'aria-label': 'App aktualisieren', 'data-click': 'app-reload' },
      svg('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' }, svg('path', { d: 'M20 8a8 8 0 0 0-14.3-2.5M4 4v4h4' }), svg('path', { d: 'M4 16a8 8 0 0 0 14.3 2.5M20 20v-4h-4' }))));
}

function tabBar(s: ShellState): HTMLElement {
  return h('nav', { class: 'wb-tabs tab-row', role: 'tablist', 'aria-label': 'Bereiche' },
    s.tabs.map(t => h('button', {
      type: 'button', role: 'tab', id: `tab-${t.id}`, 'aria-selected': String(s.tab === t.id), 'aria-controls': `panel-${t.id}`, class: 'wb-tab',
      'data-more': t.id === 'einstellungen' || t.id === 'info' ? '' : undefined, 'data-click': 'app-tab', 'data-value': t.id
    }, t.label)),
    s.pwaOffline && s.pending > 0 && h('div', { class: 'top-tools', role: 'presentation' },
      h('button', { class: 'wb-primary tool', 'data-click': 'app-sync', disabled: s.busy }, 'Jetzt synchronisieren ', h('span', { class: 'count-badge' }, s.pending))));
}

function banners(s: ShellState): Array<HTMLElement | false> {
  return [
    s.installHint && installHint(),
    !s.online && h('div', { class: 'wb-notice warning in-page offline-banner' }, s.pwaOffline
      ? 'Offlinebetrieb – Daten bleiben lokal, bis die Synchronisierung erfolgreich war.'
      : 'Keine Verbindung – in Teams, Outlook und im Browser ist die Erfassung nur online möglich.'),
    s.updateReady && h('div', { class: 'wb-notice in-page', role: 'status' }, 'Eine neue Version ist verfügbar. Bitte nach dem Speichern neu laden.',
      h('div', {}, h('button', { class: 'wb-secondary small', 'data-click': 'app-reload-now' }, 'Jetzt neu laden'))),
    !!s.message && h('div', { class: 'wb-notice in-page', role: 'status' }, s.message,
      isPopupBlocked(s.message) && h('div', {}, h('button', { class: 'wb-secondary small', 'data-click': 'app-reload-now' }, 'Erneut versuchen')))
  ];
}

/** Startseite, Anmeldeseite oder die App mit Kopfzeile und dem Inhalt des gewählten Reiters (`panel`). */
export function appView(s: ShellState, panel?: HTMLElement): HTMLElement {
  if (s.phase === 'starting') return h('main', { class: 'center' }, h('p', {}, 'App wird gestartet …'));
  if (s.phase === 'signin') {
    return h('main', { class: 'center' },
      h('div', { class: 'signin' },
        h('img', { class: 'app-icon big', src: appIconDataUri, alt: '' }),
        h('h1', {}, APP_TITLE),
        h('p', {}, 'Bitte mit dem Organisationskonto anmelden.'),
        !!(s.authError || s.message) && h('p', { class: 'wb-notice error', role: 'alert' }, s.authError || s.message),
        s.versionCheck !== 'done' && h('p', { class: 'wb-help hint', role: 'status' }, s.versionCheck === 'updating' ? 'Neue Version wird geladen …' : 'Suche nach neuer Version …'),
        h('button', { class: 'wb-primary', disabled: s.versionCheck !== 'done' || s.signingIn, 'data-click': 'app-sign-in' }, s.signingIn ? 'Weiterleitung zu Microsoft …' : s.rememberedAccount ? `Anmelden als ${s.rememberedAccount}` : 'Anmelden'),
        s.rememberedAccount && h('button', { class: 'wb-secondary', disabled: s.versionCheck !== 'done' || s.signingIn, 'data-click': 'app-sign-in-other' }, 'Anderes Konto verwenden'),
        s.installHint && installHint()));
  }
  return h('div', { class: `app-shell${s.hasSelection ? ' has-selection' : ''}` },
    h('header', { class: `topbar${s.embedded ? ' embedded' : ''}` },
      brand(s),
      s.embedded && h('h1', { class: 'sr-only' }, APP_TITLE),
      tabBar(s)),
    banners(s),
    panel);
}
